import React, { useCallback, useRef, useEffect, useState } from 'react';
import { useLocale } from '../../../locales';
import DOMPurify from 'dompurify';
import { plainTextToHtml } from '../lib/text';

interface RichTextEditorProps {
  initialContent: string;
  style: React.CSSProperties;
  onSave: (htmlContent: string, plainText: string) => void;
  onCancel: () => void;
  onContextMenu?: (event: React.MouseEvent<HTMLDivElement>) => void;
  onContentChange?: (payload: {
    htmlContent: string;
    plainText: string;
    height: number;
  }) => void;
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  initialContent,
  style,
  onSave,
  onCancel,
  onContextMenu,
  onContentChange,
}) => {
  const { t } = useLocale();
  const editorRef = useRef<HTMLDivElement>(null);
  const [hasSelection, setHasSelection] = useState(false);

  // Initialize content
  useEffect(() => {
    if (editorRef.current) {
      // Sanitize and set initial content
      const sanitized = DOMPurify.sanitize(initialContent, {
        ALLOWED_TAGS: ['b', 'strong', 'i', 'em', 'u', 'br', 'span'],
        ALLOWED_ATTR: ['style'],
      });
      editorRef.current.innerHTML = sanitized || '';
      editorRef.current.focus();
      
      // Move cursor to end
      const selection = window.getSelection();
      if (selection && editorRef.current.childNodes.length > 0) {
        const range = document.createRange();
        range.selectNodeContents(editorRef.current);
        range.collapse(false);
        selection.removeAllRanges();
        selection.addRange(range);
      }
    }
  }, [initialContent]);

  // Handle selection change
  useEffect(() => {
    const handleSelectionChange = () => {
      const selection = window.getSelection();
      setHasSelection(!!selection && !selection.isCollapsed);
    };
    document.addEventListener('selectionchange', handleSelectionChange);
    return () => {
      document.removeEventListener('selectionchange', handleSelectionChange);
    };
  }, []);

  // Apply formatting (exposed for parent to call)
  const applyFormat = useCallback((command: 'bold' | 'italic' | 'underline') => {
    document.execCommand(command, false);
    editorRef.current?.focus();
  }, []);

  // Expose applyFormat to parent via ref or event
  useEffect(() => {
    const handleApplyFormat = (e: CustomEvent) => {
      const { command } = e.detail;
      if (command === 'bold' || command === 'italic' || command === 'underline') {
        applyFormat(command);
      }
    };
    window.addEventListener('rich-text-format' as any, handleApplyFormat);
    return () => {
      window.removeEventListener('rich-text-format' as any, handleApplyFormat);
    };
  }, [applyFormat]);

  // Handle save
  const handleSave = useCallback(() => {
    if (!editorRef.current) return;
    
    const htmlContent = DOMPurify.sanitize(editorRef.current.innerHTML, {
      ALLOWED_TAGS: ['b', 'strong', 'i', 'em', 'u', 'br', 'span'],
      ALLOWED_ATTR: ['style'],
    });
    const plainText = editorRef.current.innerText || '';
    
    onSave(htmlContent, plainText);
  }, [onSave]);

  // Handle keyboard shortcuts
  const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    event.stopPropagation();

    // Save on Ctrl/Cmd + Enter
    if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      handleSave();
      return;
    }

    // Insert line break on Enter
    if (event.key === 'Enter') {
      event.preventDefault();
      document.execCommand('insertLineBreak');
      if (onContentChange && editorRef.current) {
        requestAnimationFrame(() => {
          if (!editorRef.current) return;
          onContentChange({
            htmlContent: editorRef.current.innerHTML,
            plainText: editorRef.current.innerText || '',
            height: editorRef.current.scrollHeight,
          });
        });
      }
      return;
    }

    // Cancel on Escape
    if (event.key === 'Escape') {
      event.preventDefault();
      onCancel();
      return;
    }

    // Formatting shortcuts
    if (event.metaKey || event.ctrlKey) {
      switch (event.key.toLowerCase()) {
        case 'b':
          event.preventDefault();
          applyFormat('bold');
          break;
        case 'i':
          event.preventDefault();
          applyFormat('italic');
          break;
        case 'u':
          event.preventDefault();
          applyFormat('underline');
          break;
      }
    }
  }, [handleSave, onCancel, applyFormat, onContentChange]);

  const handleInput = useCallback(() => {
    if (!editorRef.current || !onContentChange) {
      return;
    }

    onContentChange({
      htmlContent: editorRef.current.innerHTML,
      plainText: editorRef.current.innerText || '',
      height: editorRef.current.scrollHeight,
    });
  }, [onContentChange]);

  const handlePaste = useCallback(
    (event: React.ClipboardEvent<HTMLDivElement>) => {
      const text = event.clipboardData?.getData('text/plain');
      if (text === undefined || text === null) {
        return;
      }

      event.preventDefault();
      const html = plainTextToHtml(text);
      document.execCommand('insertHTML', false, html);

      if (onContentChange && editorRef.current) {
        requestAnimationFrame(() => {
          if (!editorRef.current) return;
          onContentChange({
            htmlContent: editorRef.current.innerHTML,
            plainText: editorRef.current.innerText || '',
            height: editorRef.current.scrollHeight,
          });
        });
      }
    },
    [onContentChange]
  );

  // Handle blur
  const handleBlur = useCallback(() => {
    // Small delay to allow context menu clicks
    setTimeout(() => {
      // Check if there's an active context menu
      const contextMenu = document.querySelector('.qe-context-menu');
      if (!contextMenu) {
        handleSave();
      }
    }, 150);
  }, [handleSave]);

  // Handle context menu - delegate to parent
  const handleContextMenu = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    const selection = window.getSelection();
    if (selection && !selection.isCollapsed) {
      // There's a text selection, let parent handle context menu
      if (onContextMenu) {
        onContextMenu(event);
      }
    }
  }, [onContextMenu]);

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      {/* Editable Content */}
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        className="qe-rich-text-editor"
        style={{
          ...style,
          outline: 'none',
          minHeight: '1em',
          cursor: 'text',
        }}
        onKeyDown={handleKeyDown}
        onInput={handleInput}
        onPaste={handlePaste}
        onBlur={handleBlur}
        onContextMenu={handleContextMenu}
      />

      {/* Selection hint */}
      {hasSelection && (
        <div
          style={{
            position: 'absolute',
            bottom: '-24px',
            left: '0',
            fontSize: '11px',
            color: 'rgba(255,255,255,0.5)',
            whiteSpace: 'nowrap',
          }}
        >
          {t("richTextEditor.hint")}
        </div>
      )}
    </div>
  );
};

export default RichTextEditor;
